import { lazy, type ComponentType } from "react";

type Loader<P> = () => Promise<{ default: ComponentType<P> }>;

export type PreloadableComponent<P = object> = ComponentType<P> & {
  preload: () => Promise<unknown>;
};

/**
 * `React.lazy` that can be loaded ahead of the first render.
 *
 * Every page is prerendered to static HTML, and `createRoot` replaces that HTML
 * on its first commit. With a plain `lazy` page, the first commit is the
 * Suspense fallback, so the prerendered content is wiped and the visitor stares
 * at a spinner until the page chunk arrives. That was most of the mobile
 * Lighthouse score.
 *
 * `main.tsx` calls `preload()` for the current route before rendering. Once the
 * module is in hand, the lazy factory returns a thenable that resolves
 * synchronously, which React reads without suspending, so the first commit
 * renders the real page over the identical prerendered markup.
 */
export function lazyPage<P = object>(loader: Loader<P>): PreloadableComponent<P> {
  let loaded: { default: ComponentType<P> } | undefined;
  let pending: Promise<{ default: ComponentType<P> }> | undefined;

  const preload = () => {
    if (!pending) {
      pending = loader().then((mod) => {
        loaded = mod;
        return mod;
      });
    }
    return pending;
  };

  const Component = lazy(() => {
    if (loaded) {
      const mod = loaded;
      // A thenable that calls back synchronously; React.lazy resolves it
      // during render instead of suspending.
      return { then: (resolve: (m: typeof mod) => void) => resolve(mod) } as unknown as Promise<typeof mod>;
    }
    return preload();
  }) as unknown as PreloadableComponent<P>;

  Component.preload = preload;
  return Component;
}
